import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { VENTA_PIPELINE_SERIES } from "./pygAnaliticoBuild";
import {
  buildPipelineVentaAcumChartData,
  buildPipelineVentaChartData,
} from "./pygPipelineVentaChartData";
import { useTranslation } from "react-i18next";

const PIPELINE_COLORS = (theme) => ({
  contable: theme.palette.primary.main,
  proyeccionVentas: theme.palette.info.main,
  pedidosVenta: theme.palette.warning.main,
  facturasBorrador: theme.palette.secondary.main,
});

const fmtAxis = (v) => {
  if (typeof v !== "number" || Number.isNaN(v)) return v;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(v / 1_000)}k`;
  return String(Math.round(v));
};

const chartPaperSx = (theme) => ({
  p: { xs: 1.25, sm: 1.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.85),
  backgroundColor: theme.palette.background.paper,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 6px 18px rgba(0,0,0,0.28)"
      : "0 6px 16px rgba(15,23,42,0.06)",
  height: "100%",
});

const PipelineTooltip = ({ active, payload, label, formatearImporte, t }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, e) => s + (Number(e.value) || 0), 0);
  return (
    <Paper sx={{ p: 1.25 }} elevation={4}>
      <Typography variant="caption" sx={{ fontWeight: 800, display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map((entry) => (
        <Typography
          key={entry.name}
          variant="caption"
          display="block"
          sx={{ color: entry.color }}
        >
          {entry.name}: {formatearImporte(entry.value ?? 0)}
        </Typography>
      ))}
      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 700 }}>
        {t("pygAnalitico.total")}: {formatearImporte(total)}
      </Typography>
    </Paper>
  );
};

const YearPipelinePanel = ({
  year,
  breakdown,
  formatearImporte,
  theme,
  colors,
  t,
}) => {
  const chartData = useMemo(
    () => buildPipelineVentaChartData(breakdown),
    [breakdown],
  );
  const acumData = useMemo(
    () => buildPipelineVentaAcumChartData(breakdown),
    [breakdown],
  );

  const seriesLabel = (key, fallback) =>
    t(`pygAnalitico.pipeline.${key}`, { defaultValue: fallback });

  const totales = VENTA_PIPELINE_SERIES.map(({ key, label }) => ({
    label: seriesLabel(key, label),
    total: breakdown?.[key]?.total ?? 0,
  }));

  const renderChart = (data, height = 220) => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.9)} />
        <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
        <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 10 }} width={48} />
        <Tooltip content={<PipelineTooltip formatearImporte={formatearImporte} t={t} />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {VENTA_PIPELINE_SERIES.map(({ key, label }) => (
          <Bar
            key={key}
            dataKey={key}
            name={seriesLabel(key, label)}
            stackId="venta"
            fill={colors[key]}
            maxBarSize={28}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <Paper sx={chartPaperSx(theme)}>
      <Typography variant="subtitle2" fontWeight={800} gutterBottom>
        {t("pygAnalitico.pipeline.yearTitle", { year })}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {totales.map(({ label, total }) => `${label}: ${formatearImporte(total)}`).join(" · ")}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        {t("pygAnalitico.pipeline.monthlyStacked")}
      </Typography>
      {renderChart(chartData)}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.25, mb: 0.75 }}>
        {t("pygAnalitico.pipeline.cumulative")}
      </Typography>
      {renderChart(acumData, 200)}
    </Paper>
  );
};

const PygAnaliticoPipelineVentaChart = ({
  compareYears = [],
  breakdownByYear,
  loading,
  error,
  formatearImporte,
  vistaLabel,
  marcaLabel,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const colors = PIPELINE_COLORS(theme);
  const years = useMemo(
    () => [...compareYears].sort((a, b) => a - b),
    [compareYears],
  );

  if (loading) {
    return (
      <Box sx={{ py: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary">
          {t("pygAnalitico.pipeline.loading")}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2" sx={{ py: 1 }}>
        {t("pygAnalitico.pipeline.error", { error })}
      </Typography>
    );
  }

  if (!years.length) return null;

  if (!breakdownByYear) return null;

  const filterText = `${vistaLabel || ""}${marcaLabel ? ` · ${marcaLabel}` : ""}`;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
        {t("pygAnalitico.pipeline.section")}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
        {t("pygAnalitico.pipeline.sectionHint", { filter: filterText })}
      </Typography>
      <Grid container spacing={1.5}>
        {years.map((year) => (
          <Grid item xs={12} lg={years.length >= 3 ? 4 : 6} key={year}>
            <YearPipelinePanel
              year={year}
              breakdown={breakdownByYear?.[year]}
              formatearImporte={formatearImporte}
              theme={theme}
              colors={colors}
              t={t}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PygAnaliticoPipelineVentaChart;
